import React, { useState, useEffect } from "react";
import MenuBar from "./common/MenuBar";
import ManageUserModal from "./common/ManageUserModal";
import { DataGrid } from "@mui/x-data-grid";
import toast, { Toaster } from "react-hot-toast";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  getUsers,
  selectUsers,
  saveUserDetails,
  editUserDetails,
} from "../Services/loadOrder";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

const ManageUser = () => {
  const [showManageUserModal, setShowManageUserModal] = useState(false);
  const [flag, setFlag] = useState("");
  const [initialData, setInitialData] = useState({});
  const user = JSON.parse(localStorage.getItem("user"));
  const pharmacyId = user?.pharmacyId;
  const userId = user?.userId;

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]); // keep original
  const [loading, setLoading] = useState(false);

  // search states
  const [selectedField, setSelectedField] = useState(""); // which column to search
  const [searchText, setSearchText] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const data = await getUsers(pharmacyId, userId);
      // console.log("Fetched Users Data:", data);

      const filteredRows = data.map((item, index) => ({
        id: index + 1,
        userId: item.ID,
        fullName: item.Text,
        email: item.Email,
        editedDate: item.EditedDate
          ? new Date(item.EditedDate).toLocaleString()
          : "",
        role: item.IsActive,
      }));

      setRows(filteredRows);
      setAllRows(filteredRows); // keep original copy
    };
    fetchData();
  }, [pharmacyId, userId]);

  const columns = [
    {
      field: "fullName",
      headerName: "Full Name",
      flex: 1,
      headerClassName: "pink-header",
    },
    { field: "userId", headerName: "User ID", flex: 1, headerClassName: "pink-header" },
    { field: "email", headerName: "Email", flex: 2, headerClassName: "pink-header" },
    {
      field: "role",
      headerName: "Admin Status",
      flex: 0.5,
      headerClassName: "pink-header",
      renderCell: (params) => <span>{params.value === true ? "Yes" : "No"}</span>,
    },
    {
      field: "editedDate",
      headerName: "Last Edited Date",
      flex: 1,
      headerClassName: "pink-header",
    },
    {
      field: "edit",
      headerName: "Edit",
      flex: 1,
      headerClassName: "pink-header",
      renderCell: (params) => (
        <strong>
          <button
            onClick={() => handleEdit(params.row)}
            style={{
              background: "#ec008c",
              color: "white",
              padding: "3px 6px",
              fontWeight: "bold",
              border: "1px solid #fff",
              borderRadius: "5px",
            }}
          >
            Edit
          </button>
        </strong>
      ),
    },
  ];

  // edit
  const handleEdit = async (rowData) => {
    const value = await selectUsers(pharmacyId, rowData.userId);
    setInitialData(value);
    setFlag("edit");
    setShowManageUserModal(true);
  };

  // save or edit confirm
  const handleConfirmation = async (formData) => {
    setShowManageUserModal(false);
    setLoading(true);

    if (flag === "add") {
      const response = await saveUserDetails(
        "",
        formData.FirstName,
        formData.LastName,
        formData.NetworkID,
        formData.EmailID,
        formData.AdminValue,
        pharmacyId
      );

      if (response?.Result === "User has been added successfully") {
        toast.success("User details saved successfully!");
        setLoading(false);
        window.location.reload();
      } else if (response?.Result === "User already exist") {
        toast.error("User already exists!");
        setLoading(false);
      } else {
        toast.error("Failed to save user details. Please try again.");
        setLoading(false);
      }
    } else if (flag === "edit") {
      const response = await editUserDetails(
        formData.FullName,
        formData.UserID,
        formData.FirstName,
        formData.LastName,
        formData.NetworkID,
        formData.EmailID,
        formData.AdminStatus,
        pharmacyId
      );

      if (response?.Text === "Success") {
        toast.success("User details updated successfully!");
        setLoading(false);
        window.location.reload();
      } else {
        toast.error("Failed to update user details. Please try again.");
        setLoading(false);
      }
    }
  };

  // FRONTEND SEARCH
  const handleSearch = () => {
    setSearchLoading(true);

    // if no field selected → search across few columns
    if (!selectedField && searchText.trim() !== "") {
      const q = searchText.trim().toLowerCase();
      const filtered = allRows.filter((r) => {
        return (
          (r.fullName && r.fullName.toLowerCase().includes(q)) ||
          (r.email && r.email.toLowerCase().includes(q)) ||
          (r.userId && r.userId.toString().toLowerCase().includes(q))
        );
      });
      setRows(filtered);
      setSearchLoading(false);
      return;
    }

    if (searchText.trim() === "") {
      // empty search -> show all
      setRows(allRows);
      setSearchLoading(false);
      return;
    }

    const q = searchText.trim().toLowerCase();
    const filtered = allRows.filter((r) => {
      switch (selectedField) {
        case "FullName":
          return r.fullName?.toLowerCase().includes(q);
        case "UserID":
          return r.userId?.toString().toLowerCase().includes(q);
        case "Email":
          return r.email?.toLowerCase().includes(q);
        case "AdminStatus":
          // user typed Yes/No
          const adminText = r.role === true ? "yes" : "no";
          return adminText.includes(q);
        default:
          return true;
      }
    });

    setRows(filtered);
    setSearchLoading(false);
  };

  const handleViewAll = () => {
    setSearchText("");
    setSelectedField("");
    setRows(allRows);
  };

  return (
    <div>
      <MenuBar />
      <Toaster position="top-right" />
      <div className="top-page-option">
        <div
          className="top-page-option-name"
          style={{ display: "flex", alignItems: "center" }}
        >
          <a
            href="#/home"
            style={{
              textDecoration: "none",
              color: "#ec088c",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <ChevronLeft color="#ec088c" size={18} />
            <span style={{ marginLeft: "5px" }}>Back</span>
          </a>
        </div>
        <div
          className="top-page-option-name"
          style={{ display: "flex", alignItems: "center", gap: "5px" }}
        >
          <span>Admin</span>
          <ChevronRight
            color="#ec088c"
            size={18}
            style={{ position: "relative", top: "2px" }}
          />
          <span>Manage Users</span>
        </div>
      </div>

      {/* SEARCH BAR (frontend) */}
      

      <div style={{ height: "auto", width: "87%", margin: "auto" }}>
       
       <span
        style={{
          display: "flex",
          justifyContent: "flex-end",
        //   alignItems: "",
          gap: "8px",
          marginLeft: "auto",
        //   marginRight: "6.2%",
        //   marginTop: "12px",
        //   marginBottom: "4px",
        }}
      >
        <FormControl
          size="small"
          sx={{
            minWidth: 120,
            height: "30px",
            background: "#fff",
            borderRadius: "4px",
            marginRight: "10px",
          }}
        >
          <InputLabel
            sx={{
              fontFamily: "Segoe ui",
              background: "#fff",
              color: "black",
              fontSize: "12px",
            }}
          >
            --Select--
          </InputLabel>
          <Select
            value={selectedField}
            label="--Select--"
            onChange={(e) => setSelectedField(e.target.value)}
            sx={{
              fontFamily: "Segoe ui",
              fontSize: "12px",
              border: "1px solid rgb(236, 151, 202)",
              borderRadius: "15px",
              height: "30px",
              boxShadow: "none",
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#ec008c",
                boxShadow: "0 0 6px rgba(236, 0, 140, 0.4)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#ec008c",
              },
            }}
          >
            <MenuItem
              value=""
              sx={{ fontSize: "12px", "&:hover": { backgroundColor: "#f5f5f5" } }}
            >
              --Select--
            </MenuItem>
            {/* map to your users */}
            <MenuItem
              value="FullName"
              sx={{ fontSize: "12px", "&:hover": { backgroundColor: "#f5f5f5" } }}
            >
              Full Name
            </MenuItem>
            <MenuItem
              value="UserID"
              sx={{ fontSize: "12px", "&:hover": { backgroundColor: "#f5f5f5" } }}
            >
              User ID
            </MenuItem>
            <MenuItem
              value="Email"
              sx={{ fontSize: "12px", "&:hover": { backgroundColor: "#f5f5f5" } }}
            >
              Email
            </MenuItem>
            <MenuItem
              value="AdminStatus"
              sx={{ fontSize: "12px", "&:hover": { backgroundColor: "#f5f5f5" } }}
            >
              Admin Status
            </MenuItem>
          </Select>
        </FormControl>

        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
          placeholder="Type to search..."
          style={{
            padding: "5px 5px",
            borderRadius: "15px",
            border: "1px solid rgb(236, 151, 202)",
            outline: "none",
            boxShadow: "0 1px 2px rgb(236, 151, 202)",
            transition: "all 0.3s ease",
            minWidth: "180px",
          }}
          onFocus={(e) => {
            e.target.style.border = "1px solid #ec008c";
            e.target.style.boxShadow = "0 0 6px rgba(236, 0, 140, 0.4)";
          }}
          onBlur={(e) => {
            e.target.style.border = "1px solid rgb(236, 151, 202)";
            e.target.style.boxShadow = "0 1px 2px rgb(236, 151, 202)";
          }}
        />

        <button
          onClick={handleSearch}
          disabled={searchLoading}
          style={{
            padding: "6px 18px",
            borderRadius: "4px",
            background: "#ec008c",
            color: "white",
            border: "none",
            cursor: "pointer",
          }}
        >
          Search
        </button>
        <button
          onClick={handleViewAll}
          style={{
            padding: "5px 18px",
            borderRadius: "4px",
            background: "#ec008c",
            color: "white",
            border: "none",
            cursor: "pointer",
          }}
        >
          View All
        </button>
      </span>

        <span style={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
          <button
            onClick={() => {
              setFlag("add");
              setInitialData({});
              setShowManageUserModal(true);
            }}
            style={{
              background: "#ec008c",
              color: "white",
              padding: "5px 8px",
              fontWeight: "bold",
              border: "1px solid #fff",
              borderRadius: "5px",
              marginBottom: "10px",
              cursor: "pointer",
            }}
          >
            New User
          </button>
        </span>

        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          hideFooter
          sx={{
            fontSize: "12px",
            fontFamily: "Segoe UI",
            "& .pink-header": {
              color: "#fff",
              backgroundColor: "#ec008c",
              fontWeight: "bold",
              whiteSpace: "normal !important",
              wordBreak: "break-word !important",
              overflowWrap: "break-word !important",
              lineHeight: "1.3 !important",
              textAlign: "center",
              padding: "8px 2px !important",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#f5f5f5 !important",
              cursor: "pointer",
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
              boxShadow: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none !important",
              boxShadow: "none !important",
            },
          }}
        />
      </div>

      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(255,255,255,0.7)",
            zIndex: 2000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              border: "6px solid #f3f3f3",
              borderTop: "6px solid #EC088C",
              borderRadius: "50%",
              width: 48,
              height: 48,
              animation: "spin 1s linear infinite",
            }}
          />
          <div
            style={{
              marginTop: 24,
              fontSize: "1.2rem",
              fontWeight: 500,
              color: "#222",
            }}
          >
            Your request is being processed
          </div>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% {transform: rotate(360deg);} }`}</style>
        </div>
      )}

      <ManageUserModal
        open={showManageUserModal}
        onClose={() => setShowManageUserModal(false)}
        flag={flag}
        initialData={initialData}
        onConfirm={handleConfirmation}
      />
    </div>
  );
};

export default ManageUser;
